import axiosInstance from "utils/axiosInstance";
import { WarningNetworkFailure } from "helpers/alertsweet";
const ROOT_API_URL = (
  process.env.REACT_APP_MAIN_API_URL ||
  process.env.REACT_APP_BASE_API_URL ||
  ""
).replace(/\/$/, "");
const BASE_URL = `${ROOT_API_URL}/donate/termgame`;
const USE_MOCK_GAME_API =
  process.env.REACT_APP_USE_MOCK_GAME_API === "true" || !ROOT_API_URL;

const getAuthHeaders = (token: string, businessId?: string) => ({
  ...axiosInstance.defaults.headers.common,
  authorization: "Bearer " + token,
  ...(businessId ? { b: businessId } : {}),
});

let mockGames = [
  {
    _id: "mock-game-001",
    name: "Mobile Legends",
    category: "game",
    active: "PUBLIC",
    status: "active",
    recommended: true,
    image: "/images/icon/codashop.png",
    banner: "/images/onetop-banner-2.webp",
    description: "ເຕີມ Diamond MLBB",
    sort: 1,
  },
  {
    _id: "mock-game-002",
    name: "Free Fire",
    category: "game",
    active: "PUBLIC",
    status: "active",
    recommended: false,
    image: "/images/icon/moogold.webp",
    banner: "/images/onetop-banner-2.webp",
    description: "ເຕີມ Diamond Free Fire",
    sort: 2,
  },
  {
    _id: "mock-game-003",
    name: "Netflix Gift",
    category: "streaming",
    active: "PRIVATE",
    status: "inactive",
    recommended: false,
    image: "/images/unnamed.webp",
    banner: "",
    description: "ບໍລິການ streaming package",
    sort: 3,
  },
];

const mockPackages = [
  {
    _id: "mock-package-001",
    productAgentId: "mock-game-001",
    name: "86 Diamonds",
    packageGame: "86 Diamonds",
    description: "MLBB small package",
    price: 22000,
    cost: 20000,
    active: "PUBLIC",
    sort: 1,
  },
  {
    _id: "mock-package-002",
    productAgentId: "mock-game-001",
    name: "172 Diamonds",
    packageGame: "172 Diamonds",
    description: "MLBB popular package",
    price: 43000,
    cost: 39500,
    active: "PUBLIC",
    sort: 2,
  },
  {
    _id: "mock-package-003",
    productAgentId: "mock-game-002",
    name: "100 Diamonds",
    packageGame: "100 Diamonds",
    description: "Free Fire starter package",
    price: 18000,
    cost: 16500,
    active: "PUBLIC",
    sort: 1,
  },
  {
    _id: "mock-package-004",
    productAgentId: "mock-game-003",
    name: "Premium 1 Month",
    packageGame: "Premium 1 Month",
    description: "Mock readonly package",
    price: 89000,
    cost: 78000,
    active: "PRIVATE",
    sort: 1,
  },
];

const mockDelay = async () =>
  await new Promise((resolve) => setTimeout(resolve, 200));

const sortBySort = (items: any[]) =>
  [...items].sort((a, b) => Number(a?.sort || 0) - Number(b?.sort || 0));

export const gameCreateOne = async (
  body: any,
  token: string,
  businessId?: string,
) => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    const now = Date.now();
    const nextGame = {
      _id: `mock-game-${now}`,
      recommended: false,
      ...body,
      active: body?.active || "PUBLIC",
      status: body?.active === "PUBLIC" ? "active" : "inactive",
    };
    mockGames = sortBySort([...mockGames, nextGame]);
    return nextGame;
  }

  try {
    const url = BASE_URL + "/product-agent/create-one";
    const res = await axiosInstance.post(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }

    console.error("error", error);

    throw error;
  }
};

export const gameGetMany = async ({
  findby,
  token,
  skip = 0,
  limit = 50,
  businessId,
}: {
  findby?: Findby;
  token: string;
  skip?: number;
  limit?: number;
  businessId?: string;
}): Promise<any[]> => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    const filtered = mockGames.filter((game) =>
      Object.entries(findby || {}).every(([key, value]) => {
        if (value === undefined || value === null || value === "") return true;
        return String((game as any)[key]) === String(value);
      }),
    );
    return sortBySort(filtered).slice(skip, skip + limit);
  }

  try {
    const queryParams = new URLSearchParams({
      ...(findby || {}),
      skip: skip.toString(),
      limit: limit.toString(),
    }).toString();
    const url = BASE_URL + "/product-agent/get-many?" + queryParams;
    const headers = getAuthHeaders(token, businessId);
    const res = await axiosInstance.get(url, {
      headers: headers,
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }

    console.error("error", error);
    throw error;
  }
};

export const gameGetCount = async ({
  findby,
  token,
  businessId,
}: {
  findby?: Findby;
  token: string;
  businessId?: string;
}): Promise<{ count: number }> => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    return { count: mockGames.length };
  }

  try {
    const queryParams = new URLSearchParams({
      ...(findby || {}),
    }).toString();
    const url = `${BASE_URL}/product-agent/get-count?${queryParams}`;
    const res = await axiosInstance.get(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }

    console.error("error", error);
    throw error;
  }
};

export const gameGetOne = async ({
  id,
  token,
  businessId,
}: {
  id: string;
  token: string;
  businessId?: string;
}): Promise<any> => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    return mockGames.find((game) => game._id === id) || null;
  }

  try {
    const url = `${BASE_URL}/product-agent/get-one?id=${id}`;
    const headers = getAuthHeaders(token, businessId);
    const res = await axiosInstance.get(url, { headers });
    console.log("res.data:", res.data);
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }

    console.error("error", error);
    throw error;
  }
};

export const gameUpdateOne = async (
  id: string,
  body: any,
  token: string,
  businessId?: string,
) => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    let updatedGame: any = null;
    mockGames = mockGames.map((game) => {
      if (game._id !== id) return game;
      updatedGame = {
        ...game,
        ...body,
        status:
          body?.active === "PUBLIC"
            ? "active"
            : body?.active === "PRIVATE"
              ? "inactive"
              : game.status,
      };
      return updatedGame;
    });
    return updatedGame || { error: true, message: "Mock game not found" };
  }

  try {
    const url = `${BASE_URL}/product-agent/update-one?id=${id}`;
    const res = await axiosInstance.put(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }

    console.error("error", error);
    return { error: true, message: error.message };
  }
};

export const gameDeleteOne = async (
  id: string,
  token: string,
  businessId?: string,
) => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    mockGames = mockGames.filter((game) => game._id !== id);
    return { success: true };
  }

  try {
    const url = `${BASE_URL}/product-agent/delete-one?id=${id}`;
    const res = await axiosInstance.delete(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }

    console.error("error", error);
    return { error: true, message: error.message };
  }
};

export const gamePackageGetMany = async ({
  findby,
  token,
  skip = 0,
  limit = 50,
  businessId,
}: {
  findby?: Record<string, any>;
  token: string;
  skip?: number;
  limit?: number;
  businessId?: string;
}): Promise<any[]> => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    const filtered = mockPackages.filter((pkg) =>
      Object.entries(findby || {}).every(([key, value]) => {
        if (value === undefined || value === null || value === "") return true;
        return String((pkg as any)[key]) === String(value);
      }),
    );
    return sortBySort(filtered).slice(skip, skip + limit);
  }

  try {
    const queryParams = new URLSearchParams({
      ...(findby || {}),
      skip: skip.toString(),
      limit: limit.toString(),
    }).toString();
    const url = `${BASE_URL}/product-agent-package/get-many?${queryParams}`;
    const headers = getAuthHeaders(token, businessId);
    const res = await axiosInstance.get(url, { headers });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }
    console.error("error", error);
    throw error;
  }
};

export const gamePackageCreateOne = async (
  body: any,
  token: string,
  businessId?: string,
) => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    return {
      error: true,
      message: "Mock API ເປີດໃຫ້ເບິ່ງລາຄາແພັກເກດເທົ່ານັ້ນ",
    };
  }

  try {
    const url = `${BASE_URL}/product-agent-package/create-one`;
    const res = await axiosInstance.post(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }
    console.error("error", error);
    return { error: true, message: error.message };
  }
};

export const gamePackageUpdateOne = async (
  id: string,
  body: any,
  token: string,
  businessId?: string,
) => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    return {
      error: true,
      message: "Mock API ເປີດໃຫ້ເບິ່ງລາຄາແພັກເກດເທົ່ານັ້ນ",
    };
  }

  try {
    const url = `${BASE_URL}/product-agent-package/update-one?id=${id}`;
    const res = await axiosInstance.put(url, body, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }
    console.error("error", error);
    return { error: true, message: error.message };
  }
};

export const gamePackageDeleteOne = async (
  id: string,
  token: string,
  businessId?: string,
) => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    return {
      error: true,
      message: "Mock API ເປີດໃຫ້ເບິ່ງລາຄາແພັກເກດເທົ່ານັ້ນ",
    };
  }

  try {
    const url = `${BASE_URL}/product-agent-package/delete-one?id=${id}`;
    const res = await axiosInstance.delete(url, {
      headers: getAuthHeaders(token, businessId),
    });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }
    console.error("error", error);
    return { error: true, message: error.message };
  }
};

export const walletGetMe = async ({
  token,
  businessId,
}: {
  token: string;
  businessId?: string;
}): Promise<any> => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    return {
      balance: 1285000,
      gameRevenue: 342000,
      gameWebsiteUrl: "https://donate.onetop.la/topup/mock-business",
      gameWebsiteDescription:
        "Mock API: ລິ້ງເວັບເຕີມເກມ ແລະ ລາຍໄດ້ຈະມາຈາກ backend ຈິງເມື່ອພ້ອມ",
    };
  }

  try {
    const url = `${BASE_URL}/wallet/get-me`;
    const headers = getAuthHeaders(token, businessId);
    const res = await axiosInstance.get(url, { headers });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }
    console.error("error", error);
    throw error;
  }
};

export const gameGetConditions = async ({
  token,
  businessId,
}: {
  token: string;
  businessId?: string;
}): Promise<any> => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    return {
      markdown:
        "## Mock policy\n\n- ຂໍ້ມູນນີ້ແມ່ນ mock ເພື່ອທົດສອບ UI.\n- ການຄິດໄລ່ລາຍໄດ້ຈິງຕ້ອງຢືນຢັນຈາກ backend.",
    };
  }

  try {
    const url = `${BASE_URL}/policy`;
    const headers = getAuthHeaders(token, businessId);
    const res = await axiosInstance.get(url, { headers });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }
    console.error("error", error);
    throw error;
  }
};

export const gamePackageGetOne = async ({
  id,
  token,
  businessId,
}: {
  id: string;
  token: string;
  businessId?: string;
}): Promise<any> => {
  if (USE_MOCK_GAME_API) {
    await mockDelay();
    return mockPackages.find((pkg) => pkg._id === id) || null;
  }

  try {
    const url = `${BASE_URL}/product-agent-package/get-one?id=${id}`;
    const headers = getAuthHeaders(token, businessId);
    const res = await axiosInstance.get(url, { headers });
    return res.data;
  } catch (error: any) {
    if (error.message === "Network Error") {
      WarningNetworkFailure();
    }
    console.error("error", error);
    throw error;
  }
};
